import * as React from "react";

export type SparklinePoint = {
    value: number
    isProvisional?: boolean
};

type SparklineProps = {
    points: SparklinePoint[]
    width?: number
    height?: number
    label?: string // accessible description, e.g. "Infrastructure, last 12 months"
};

const PADDING = 2;

/**
 * A small line showing the course of a value series, e.g. next to a table row. The scale always
 * includes zero, so a stable value reads as a flat line and not as noise. Segments leading to a
 * provisional point are dashed.
 */
const Sparkline: React.FC<SparklineProps> = ({points, width = 64, height = 20, label}) => {
    if (points.length === 0) {
        return <svg width={width} height={height} aria-hidden="true"/>;
    }

    const values = points.map(point => point.value);
    const minimum = Math.min(0, ...values);
    const maximum = Math.max(0, ...values);
    const range = maximum - minimum;
    const innerHeight = height - 2 * PADDING;
    const step = points.length > 1 ? (width - 2 * PADDING) / (points.length - 1) : 0;

    const coordinates = points.map((point, index) => ({
        x: PADDING + index * step,
        y: PADDING + (range === 0 ? innerHeight : innerHeight - ((point.value - minimum) / range) * innerHeight),
        isProvisional: point.isProvisional === true,
    }));

    const segments = coordinates.slice(1).map((end, index) => ({
        start: coordinates[index],
        end,
        isProvisional: end.isProvisional || coordinates[index].isProvisional,
    }));
    const last = coordinates[coordinates.length - 1];

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} className="overflow-visible text-gray-500">
            {segments.map((segment, index) => (
                <line
                    key={index}
                    x1={segment.start.x}
                    y1={segment.start.y}
                    x2={segment.end.x}
                    y2={segment.end.y}
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeDasharray={segment.isProvisional ? '2 2' : undefined}
                />
            ))}
            <circle cx={last.x} cy={last.y} r={2} fill={last.isProvisional ? 'white' : 'currentColor'} stroke="currentColor" strokeWidth={1}/>
        </svg>
    );
};

export default Sparkline;
